import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '../components/common/PageHeader';
import { listPages, updatePage } from '../services/siteService';
import { getErrorMessage } from '../services/apiClient';
import { useToast } from '../context/ToastContext';

const PAGE_LABELS = {
  terms: 'Terms of Service',
  'privacy-policy': 'Privacy Policy',
};

export default function LegalPagesPage() {
  const { showToast } = useToast();
  const [pages, setPages] = useState({});
  const [activeSlug, setActiveSlug] = useState('terms');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadPages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listPages();
      const map = {};
      (res.data || []).forEach((p) => {
        map[p.slug] = { title: p.title, content: p.content };
      });
      setPages(map);
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadPages();
  }, [loadPages]);

  const form = pages[activeSlug] || { title: '', content: '' };

  const setForm = (field, value) => {
    setPages((prev) => ({
      ...prev,
      [activeSlug]: { ...prev[activeSlug], [field]: value },
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title?.trim() || !form.content?.trim()) {
      showToast('Title and content are required', 'error');
      return;
    }
    setSaving(true);
    try {
      await updatePage(activeSlug, form);
      showToast(`${PAGE_LABELS[activeSlug]} saved`);
      loadPages();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Legal Pages"
        description="Edit Terms of Service and Privacy Policy shown on the storefront"
      />

      <div className="tabs mb-4" style={{ display: 'flex', gap: '8px' }}>
        {Object.entries(PAGE_LABELS).map(([slug, label]) => (
          <button
            key={slug}
            type="button"
            className={`btn ${activeSlug === slug ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setActiveSlug(slug)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="card">
        {loading ? (
          <p className="p-6">Loading...</p>
        ) : (
          <form onSubmit={handleSave} className="p-6">
            <div className="form-group full mb-4">
              <label>Page Title</label>
              <input
                className="input"
                value={form.title || ''}
                onChange={(e) => setForm('title', e.target.value)}
                required
              />
            </div>
            <div className="form-group full mb-4">
              <label>Content</label>
              <textarea
                className="input textarea"
                rows={18}
                value={form.content || ''}
                onChange={(e) => setForm('content', e.target.value)}
                required
                placeholder="Plain text content. Line breaks are preserved."
              />
            </div>
            <p className="text-sm text-muted mb-4">
              Storefront URL:{' '}
              <code>/{activeSlug}</code>
            </p>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Page'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
